// GuidedTour (Spec §6.3 / P4) — a first-run spotlight walkthrough. Each step
// optionally targets an element by `data-tour="<key>"`; if found, the element is
// spotlit and the card anchors near it, otherwise the card centres. Dismissible,
// resumable from Help, with a per-user "seen" flag in localStorage so it auto-
// starts only once. Focus-trapped-ish (Esc closes) and announces step count.

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { X, ArrowRight, ArrowLeft } from 'lucide-react'

export interface TourStep {
  /** matches an element rendered with data-tour="<target>" */
  target?: string
  title: string
  body: ReactNode
  /** Runs just before this step is shown — lets the host switch a sub-view,
   * select demo data, etc. so the step's target exists by the time GuidedTour
   * looks for it. May be sync or return a promise; either way GuidedTour
   * retries its target lookup for a short window rather than searching once,
   * since a host state change (e.g. setView) commits its DOM asynchronously. */
  onBeforeStep?: () => void | Promise<void>
}

interface GuidedTourProps {
  tourId: string
  steps: TourStep[]
  /** controlled open (e.g. resumed from Help). Omit to rely on autoStart. */
  open?: boolean
  onClose?: () => void
  /** auto-open on first visit for this user (default true) */
  autoStart?: boolean
}

const SEEN_PREFIX = 'guidedTourSeen:'

export function tourSeenKey(tourId: string): string {
  // Per-user: the auth token subject is not needed here because localStorage is
  // already per-browser-profile; but we still namespace by the active user id if
  // present so multiple accounts on one machine don't share seen-state.
  let uid = ''
  try {
    uid = localStorage.getItem('activeWorkspaceOwner') || localStorage.getItem('userId') || ''
  } catch {
    /* ignore */
  }
  return `${SEEN_PREFIX}${uid}:${tourId}`
}

export function hasSeenTour(tourId: string): boolean {
  try {
    return localStorage.getItem(tourSeenKey(tourId)) === '1'
  } catch {
    return false
  }
}

export function markTourSeen(tourId: string): void {
  try {
    localStorage.setItem(tourSeenKey(tourId), '1')
  } catch {
    /* non-fatal */
  }
}

export default function GuidedTour({ tourId, steps, open, onClose, autoStart = true }: GuidedTourProps) {
  const controlled = open !== undefined
  const [internalOpen, setInternalOpen] = useState(false)
  const [idx, setIdx] = useState(0)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const cardRef = useRef<HTMLDivElement>(null)

  // Auto-start once per user (uncontrolled mode only).
  useEffect(() => {
    if (controlled || !autoStart) return
    if (!hasSeenTour(tourId) && steps.length > 0) setInternalOpen(true)
  }, [controlled, autoStart, tourId, steps.length])

  const isOpen = controlled ? !!open : internalOpen

  // Reset to the first step whenever the tour (re)opens.
  useEffect(() => {
    if (isOpen) setIdx(0)
  }, [isOpen])

  const close = () => {
    markTourSeen(tourId)
    if (controlled) onClose?.()
    else setInternalOpen(false)
  }

  // Run the step's host hook (if any), then locate its target for the
  // spotlight. onBeforeStep may trigger an async host state change (e.g. a
  // parent's setView switching sub-views) whose DOM doesn't exist yet on the
  // very next tick, so the lookup retries for a short window instead of
  // giving up after one query.
  useLayoutEffect(() => {
    if (!isOpen) return
    const step = steps[idx]
    let cancelled = false
    setRect(null)

    const findTarget = () => {
      if (cancelled || !step?.target) return
      let attempts = 0
      const tryFind = () => {
        if (cancelled) return
        const el = document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`)
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' })
          setTimeout(() => {
            if (!cancelled) setRect(el.getBoundingClientRect())
          }, 200)
        } else if (attempts < 80) {
          // Up to ~4s — long enough for a host's onBeforeStep to switch a
          // sub-view AND a live data fetch (e.g. selecting a demo group) to
          // resolve before this step gives up and centres itself instead.
          attempts += 1
          setTimeout(tryFind, 50)
        }
      }
      tryFind()
    }

    Promise.resolve(step?.onBeforeStep?.()).then(findTarget)
    return () => {
      cancelled = true
    }
  }, [isOpen, idx, steps])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      if (e.key === 'ArrowRight') setIdx((i) => Math.min(i + 1, steps.length - 1))
      if (e.key === 'ArrowLeft') setIdx((i) => Math.max(i - 1, 0))
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, steps.length])

  if (!isOpen || steps.length === 0) return null
  const step = steps[idx]
  const last = idx === steps.length - 1

  // Card position: near the target if we have one, else centred.
  const cardStyle: React.CSSProperties = rect
    ? {
        position: 'fixed',
        top: Math.min(rect.bottom + 12, window.innerHeight - 220),
        left: Math.min(Math.max(rect.left, 16), window.innerWidth - 360),
        width: 340,
      }
    : {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 360,
      }

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label={`Guided tour, step ${idx + 1} of ${steps.length}`}>
      {/* Dim + spotlight cut-out via a box-shadow ring around the target. */}
      <div className="absolute inset-0 bg-slate-900/50" onClick={close} aria-hidden />
      {rect && (
        <div
          aria-hidden
          className="pointer-events-none absolute rounded-lg ring-2 ring-iflow-400"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            boxShadow: '0 0 0 9999px rgba(15,23,42,0.5)',
          }}
        />
      )}
      <div ref={cardRef} style={cardStyle} className="rounded-xl border border-slate-200 bg-white p-4 shadow-2xl">
        <div className="mb-1 flex items-start justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-iflow-600">
            Step {idx + 1} of {steps.length}
          </p>
          <button type="button" onClick={close} aria-label="Close tour" className="text-slate-400 hover:text-slate-600">
            <X size={16} />
          </button>
        </div>
        <h3 className="text-base font-semibold text-iflow-900">{step.title}</h3>
        <div className="mt-1 text-sm leading-relaxed text-slate-600">{step.body}</div>
        <div className="mt-4 flex items-center justify-between">
          <button type="button" onClick={close} className="text-sm text-slate-500 hover:text-slate-700">
            Skip
          </button>
          <div className="flex items-center gap-2">
            {idx > 0 && (
              <button
                type="button"
                onClick={() => setIdx((i) => Math.max(i - 1, 0))}
                className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm text-iflow-700 hover:bg-iflow-50"
              >
                <ArrowLeft size={14} /> Back
              </button>
            )}
            <button
              type="button"
              onClick={() => (last ? close() : setIdx((i) => Math.min(i + 1, steps.length - 1)))}
              className="inline-flex items-center gap-1 rounded-lg bg-iflow-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-iflow-700"
            >
              {last ? 'Done' : 'Next'} {!last && <ArrowRight size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
